import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import {
    FullImageElement,
    IconElement,
    ImageElement,
    ProductService
} from '../../../shared/services/catalog.service';
import {
    FileUploaderComponent,
    HcModal,
    HcSort,
    HcToastOptions,
    HcToasterService,
    ModalOptions,
    ModalService,
    PaginationComponent,
    SelectChangeEvent,
    SimpleModalOptions,
    TabChangeEvent
} from '@healthcatalyst/cashmere';
import { ChangeElement, ChangeService } from '../../../shared/services/change.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { DocumentService } from '../../../shared/services/document.service';
import { LightboxComponent } from '../../lightbox/lightbox.component';
import { ComponentCanDeactivate } from '../../can-deactivate/component-can-deactivate';
import { OktaService } from '../../../shared/services/okta.service';
import { TechnologyElement, TechnologyService } from '../../../shared/services/technology.service';
import { TypeElement, TypeService } from '../../../shared/services/type.service';
import { ProductLineElement, ProductLineService } from '../../../shared/services/product-line.service';
import { StatusElement, StatusService } from '../../../shared/services/status.service';
import { SLAElement, SLAService } from '../../../shared/services/sla.service';
import { ItemService } from '../../../shared/services/item.service';
import { SuiteElement, SuiteService } from '../../../shared/services/suites.service';
import { ServiceElement, ServiceService } from '../../../shared/services/service.service';
import { AdminChangeLogComponent } from '../../individual-change-log/admin-change-log.component';
import { environment } from '../../../../environments/environment';
import { solutionCapabilities } from '../../../shared/solutionCapabilities';

/** Admin product element editor */
@Component({
    selector: 'pd-admin-suites-editor',
    templateUrl: './admin-suites-editor.component.html',
    styleUrls: ['./admin-suites-editor.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminSuitesEditorComponent extends ComponentCanDeactivate implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    modalRef: HcModal<TemplateRef<any>>;
    selectedTab = 0;
    suite: SuiteElement;
    suiteLoading = true;
    suiteError = false;
    imagesEdited = false;
    suiteID: number;
    itemID: number;
    savedItem = -1;
    revisionDate = '';
    solutionCapabilitiesList = solutionCapabilities;
    suiteForm = new FormGroup({
        nameControl: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        previousNamesControl: new FormArray([new FormControl('', { nonNullable: true })]),
        IDControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        typeControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        productLineControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        statusControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        slaControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        published: new FormControl(false, { nonNullable: true }),
        taglineControl: new FormControl('', { nonNullable: true }),
        problemControl: new FormControl('', { nonNullable: true }),
        approachControl: new FormControl('', { nonNullable: true }),
        benefitsFeaturesControl: new FormControl('', { nonNullable: true }),
        iconSVGControl: new FormControl('', { nonNullable: true }),
        iconPNGControl: new FormControl('', { nonNullable: true }),
        iconBase64Control: new FormControl('', { nonNullable: true }),
        techDescControl: new FormControl('', { nonNullable: true }),
        docsUrlControl: new FormControl('', { nonNullable: true }),
        communityUrlControl: new FormControl('', { nonNullable: true }),
        assocTechControl: new FormControl<TechnologyElement[]>([], { nonNullable: true }),
        assocServicesControl: new FormControl<ServiceElement[]>([], { nonNullable: true }),
        shortDescription: new FormControl('', { nonNullable: true }),
        subtitle: new FormControl('', { nonNullable: true }),
        solutionArea: new FormControl(1, { nonNullable: true }),
        solutionCapability: new FormControl('', { nonNullable: true })
    });

    adminCaseStudiesControl = new FormGroup({
        id: new FormControl(0, { nonNullable: true }),
        title: new FormControl('', { nonNullable: true }),
        url: new FormControl('', { nonNullable: true }),
        metrics: new FormArray([new FormControl('', { nonNullable: true })])
    });

    selectedImageFiles = new FormControl();
    imageForm = new FormGroup({});
    imgID = 0;
    images: FullImageElement[] = [];
    imagesToDelete: string[] = [];
    imageUploadComplete: boolean;
    quillConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean'], ['link']]
    };
    quillCSConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], ['clean'], ['link']]
    };
    quillStyles = {
        'font-family': "'Noto Sans', 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif"
    };
    tech: TechnologyElement[] = [];
    services: ServiceElement[] = [];
    types: TypeElement[] = [];
    productLines: ProductLineElement[] = [];
    statuses: StatusElement[] = [];
    SLAs: SLAElement[] = [];

    saving = false;

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    @ViewChild('fileUploaderControl')
    fileUploaderControl: FileUploaderComponent;

    @ViewChild('changeLog')
    changeLog: AdminChangeLogComponent;

    get loading(): boolean {
        return this.suiteLoading;
    }

    get loadError(): boolean {
        return this.suiteError;
    }

    get suiteInputsDirty(): boolean {
        return this.suiteForm.dirty || this.imageForm.dirty || this.imagesEdited;
    }

    constructor(
        private route: ActivatedRoute,
        private productService: ProductService,
        private suiteService: SuiteService,
        private changeService: ChangeService,
        private modalService: ModalService,
        private toasterService: HcToasterService,
        private documentService: DocumentService,
        private oktaService: OktaService,
        private typeService: TypeService,
        private productLineService: ProductLineService,
        private statusService: StatusService,
        private SLAService: SLAService,
        private itemService: ItemService,
        private technologyService: TechnologyService,
        private serviceService: ServiceService
    ) {
        super();
    }

    ngOnInit(): void {
        this.route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
            // If a suite ID is supplied, we're editing an existing suite
            if (params['id']) {
                this.suiteID = params['id'];
            } else {
                // Otherwise we're creating a new suite so generate a new item ID for the suite
                this.itemService.getNewID().pipe(takeUntil(this.unsubscribe)).subscribe({
                    next: data => {                            
                        this.itemID = data['itemNumber'];
                        this.suiteForm.controls['IDControl'].setValue(data['itemNumber']);
                        this.suiteError = false;
                        this.suiteLoading = false;
                    },
                    error: msg => { 
                        console.log( msg );
                        this.suiteError = true;
                        this.suiteLoading = false;
                    }
                });
            }
        });

        this.typeService.catTypesUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.types = this.sortAlphabetically(list);
                if (this.productLines.length !== 0 && this.statuses.length !== 0 && this.SLAs.length !== 0 && this.tech.length !== 0 && this.services.length !== 0) {
                    this.suiteID && this.loadSuite();
                }
            } else {
                !this.typeService.loadFailed ? this.typeService.loadCatTypes('suite') : this.suiteError = true;
            }
        });

        this.productLineService.productLineListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.productLines = this.sortAlphabetically(list);
                if (this.types.length !== 0 && this.statuses.length !== 0 && this.SLAs.length !== 0 && this.tech.length !== 0 && this.services.length !== 0) {
                    this.suiteID && this.loadSuite();
                }
            } else {
                !this.productLineService.loadFailed ? this.productLineService.refresh() : this.suiteError = true;
            }
        });


        this.statusService.statusListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statuses = this.sortAlphabetically(list);
                if (this.types.length !== 0 && this.productLines.length !== 0 && this.SLAs.length !== 0 && this.tech.length !== 0 && this.services.length !== 0) {
                    this.suiteID && this.loadSuite();
                }
            } else {
                !this.statusService.loadFailed ? this.statusService.refresh() : this.suiteError = true;
            }
        });

        this.SLAService.slaListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.SLAs = this.sortAlphabetically(list);
                for (let i = 0; i < this.SLAs.length; i++) {
                    if (this.SLAs[i].name === 'Health Catalyst') {
                        this.suiteForm.controls['slaControl'].setValue(this.SLAs[i].slaid);
                        break;
                    }
                }
                if (this.types.length !== 0 && this.productLines.length !== 0 && this.statuses.length !== 0 && this.tech.length !== 0 && this.services.length !== 0) {
                    this.suiteID && this.loadSuite();
                }
            } else {
                !this.SLAService.loadFailed ? this.SLAService.refresh() : this.suiteError = true;
            }
        });

        this.technologyService.technologyUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.tech = this.sortAlphabetically(list);
                if (this.types.length !== 0 && this.productLines.length !== 0 && this.statuses.length !== 0 && this.SLAs.length !== 0 && this.services.length !== 0) {
                    this.suiteID && this.loadSuite();
                }
            } else {
                !this.technologyService.loadFailed ? this.technologyService.refresh() : this.suiteError = true;
            }
        });

        this.serviceService.servicesUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.services = this.sortAlphabetically(list);
                if (this.types.length !== 0 && this.productLines.length !== 0 && this.statuses.length !== 0 && this.SLAs.length !== 0 && this.tech.length !== 0) {
                    this.suiteID && this.loadSuite();
                }
            } else {
                !this.serviceService.loadFailed ? this.serviceService.refresh() : this.suiteError = true;
            }
        });
    }

    //alphabetical sort helper function
    sortAlphabetically(list: any[]) {
        return list.sort((a, b) =>
            a.name.localeCompare(b.name)
        )
    }


    //check if the Item ID is already in use and show an error on the form field if it is
    checkID( uploadModal?: TemplateRef<any> ): void {
        const currentID = this.suiteForm.controls['IDControl'].value!;

        if ( this.savedItem !== currentID ) {
            this.itemService.checkID(currentID).pipe(takeUntil(this.unsubscribe)).subscribe({
                next: data => {        
                    if ( data['itemExists'] ) {
                        this.suiteForm.controls['IDControl'].setErrors({ valid: false });
                    } else if( uploadModal ) {
                        this.onClickSave(uploadModal);
                    }
                },
                error: msg => { 
                    console.log( msg );
                }
            });
        } else if ( uploadModal ) {
            this.onClickSave(uploadModal);
        }
    }

    loadImages(imageArray: ImageElement[]) {
        let productImages: FullImageElement[] = [];
        imageArray.forEach((img: ImageElement) => {
            const placementVal = img.placement ? img.placement : (img.visibleInSheet ? 'Body' : 'Hidden');
            productImages.push({
                id: this.imgID,
                uploadID: img.id,
                type: img.type,
                caption: img.caption,
                fileName: img.fileName,
                visibleInSheet: img.visibleInSheet || false,
                uploadState: 2,
                placement: placementVal
            });

            this.imageForm.addControl('caption' + this.imgID, new FormControl(img.caption.length > 0 ? img.caption : ''));
            this.imageForm.addControl('placement' + this.imgID, new FormControl(placementVal));
            this.imgID++;
        });
        this.images = productImages;
    }

    checkPlacement( event: SelectChangeEvent, index: number ) {
        const placement = event.value;
        if ( placement !== 'Hidden' ) {
            for (let i = 0; i < this.images.length; i++) {
                if ( this.imageForm.get('placement' + this.images[i].id)!.value === placement && i !== index ) {
                    this.imageForm.get('placement' + this.images[i].id)!.setValue('Hidden');
                }
            }
        }
    }

    loadSuite(): void {
        this.suiteService
            .detail(this.suiteID)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: data => {
                    this.revisionDate = data.revisionDate;
                    this.itemID = data.item;
                    this.savedItem = data.item;

                    //parse previous names, images, and icons strings from database
                    if (data.previousNames) {
                        data.previousNames = JSON.parse(data.previousNames);
                        //create form fields and patch previous names

                        //if the number of controls don't match the number of previous names, set up the controls
                        if (data.previousNames.length !== this.suiteForm.controls.previousNamesControl.controls.length || data.previousNames.length === 1) {
                            for (let i = 0; i < data.previousNames.length; i++) {
                                if (i === 0) {
                                    this.suiteForm.controls.previousNamesControl.controls[i].patchValue(data.previousNames[0]);
                                } else {
                                    this.suiteForm.controls.previousNamesControl.push((new FormControl(data.previousNames[i], { nonNullable: true })));
                                }
                            }
                        }
                    }

                    if (data.images) {
                        const imgData = JSON.parse(data.images);
                        this.loadImages(imgData);
                    }

                    if (data.icons) {
                        data.icons = JSON.parse(data.icons);
                    }

                    //get associated tech values for multiselect
                    let associatedTech: TechnologyElement[] = [];
                    if (data.associatedTechnology) {
                        data.associatedTechnology.forEach((element: TechnologyElement) => {
                            for (let i = 0; i < this.tech.length; i++) {
                                if (element.technologyID === this.tech[i].technologyID) {
                                    associatedTech.push(this.tech[i]);
                                    break;
                                }
                            }
                        });
                    }

                    if (data.associatedAccelerators) {
                        data.associatedAccelerators.forEach((element: TechnologyElement) => {
                            for (let i = 0; i < this.tech.length; i++) {
                                if (element.technologyID === this.tech[i].technologyID) {
                                    associatedTech.push(this.tech[i]);
                                    break;
                                }
                            }
                        });
                    }

                    //get associated service values for multiselect
                    let associatedServices: ServiceElement[] = [];
                    if (data.associatedServices) {
                        data.associatedServices.forEach((element: ServiceElement) => {
                            for (let i = 0; i < this.services.length; i++) {
                                if (element.serviceID === this.services[i].serviceID) {
                                    associatedServices.push(this.services[i]);
                                    break;
                                }
                            }
                        });
                    }

                    this.suiteError = false;
                    this.suiteLoading = false;

                    //patch for fields with suite data
                    this.suiteForm.patchValue({
                        nameControl: data.name,
                        IDControl: data.item,
                        typeControl: data.typeID,
                        productLineControl: data.productLineID,
                        statusControl: data.statusID,
                        slaControl: data.slaid,
                        published: data.published,
                        taglineControl: data.tagline,
                        problemControl: data.problem,
                        approachControl: data.approach,
                        benefitsFeaturesControl: data.features,
                        iconSVGControl: data.icons['svgURL'],
                        iconPNGControl: data.icons['pngURL'],
                        iconBase64Control: data.icons['base64URL'],
                        techDescControl: data.techDescription,
                        docsUrlControl: data.docsURL,
                        communityUrlControl: data.communityURL,
                        assocTechControl: associatedTech,
                        assocServicesControl: associatedServices,
                        shortDescription: data.shortDescription,
                        subtitle: data.subtitle,
                        solutionArea: data.solutionArea ? data.solutionArea : 1,
                        solutionCapability: data.solutionCapability ? data.solutionCapability : this.solutionCapabilitiesList[(data.solutionArea ? data.solutionArea : 1)-1][0].id
                    });

                    this.suite = {
                        suiteID: data.suiteID,
                        name: data.name,
                        item: data.item,
                        typeID: data.typeID,
                        productLineID: data.productLineID,
                        statusID: data.statusID,
                        slaid: data.slaid,
                        published: data.published,
                        revisionDate: data.revisionDate,
                        tagline: data.tagline,
                        problem: data.problem,
                        approach: data.approach,
                        features: data.features,
                        images: data.images,
                        icons: data.icons,
                        techDescription: data.techDescription,
                        docsURL: data.docsURL,
                        communityURL: data.communityURL,
                        associatedTechnology: associatedTech,
                        associatedServices: associatedServices,
                        previousNames: data.previousNames,
                        shortDescription: data.shortDescription,
                        subtitle: data.subtitle,
                        solutionArea: data.solutionArea,
                        solutionCapability: data.solutionCapability
                    };

                    this.changeLog.loadChanges(this.itemID, "suite");

                    this.suiteForm.markAsPristine();
                    this.imageForm.markAsPristine();
                },
                error: msg => {
                    this.suiteError = true;
                    this.suiteLoading = false;
                    console.log(msg);
                }
            });
    }

    elementSearch(term: string, item: TechnologyElement | ServiceElement): boolean {
        term = term.toLowerCase();
        return item.name.toLowerCase().includes(term);
    }

    ////Previous Name Functions////
    addPrevName() {
        this.suiteForm.controls.previousNamesControl.push(new FormControl('', { nonNullable: true }));
        this.suiteForm.controls.previousNamesControl.markAsDirty();
    }

    removePrevName(index: number) {
        this.suiteForm.controls.previousNamesControl.removeAt(index);
        this.suiteForm.controls.previousNamesControl.markAsDirty();
    }
    ////End Previous Name Functions////

    ////Image Functions////
    //add images and corresponding form controls to images table
    addImages() {
        //check that all files are images
        let fileCheckFail = false;
        for (let i = 0; i < this.selectedImageFiles.value.length; i++) {
            if (!this.selectedImageFiles.value[i].type.includes('image/')) {
                fileCheckFail = true;
                break;
            }
        }

        if (fileCheckFail) {
            this.selectedImageFiles.markAsTouched();
            this.selectedImageFiles.setErrors({ invalid: true });
        } else {
            for (let i = 0; i < this.selectedImageFiles.value.length; i++) {
                // add form controls and update the array of files to save
                this.imageForm.addControl('caption' + this.imgID, new FormControl(''));
                this.imageForm.addControl('placement' + this.imgID, new FormControl('Hidden'));
                this.images.push({
                    id: this.imgID,
                    uploadID: '',
                    caption: '',
                    type: this.selectedImageFiles.value[i].type,
                    fileName: this.selectedImageFiles.value[i].name,
                    visibleInSheet: false,
                    data: this.selectedImageFiles.value[i],
                    uploadState: 0,
                    placement: 'Hidden'
                });
                this.imgID++;
            }

            this.imagesEdited = true;

            //clear selected images
            if (this.fileUploaderControl) {
                setTimeout(() => {
                    this.fileUploaderControl.reset();
                });
            }
            this.selectedImageFiles = new FormControl();
        }
    }

    //open confirm removal modal, if the user confirms then remove the image reference
    confirmRemoveImage(img: FullImageElement) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the image: <strong>' + img.fileName + '</strong>?<p>',
            headerText: 'Remove Image',
            confirmButtonText: 'Remove Image',
            icon: ''
        };
        this.modalService
            .confirmDestructive(options)
            .pipe(
                take(1),
                filter(confirmed => confirmed)
            )
            .subscribe(() => {
                this.removeImageRef(img);
            });
    }

    //remove the image from the list of images and add it to the array of images to delete if it was already uploaded
    removeImageRef(img: FullImageElement) {
        let imgIndex = -1;
        for (let i = 0; i < this.images.length; i++) {
            if (img.id === this.images[i].id) {
                imgIndex = i;
                break;
            }
        }
        if (imgIndex > -1) {
            this.images.splice(imgIndex, 1);
            if (img.uploadID) {
                this.imagesToDelete.push(img.uploadID);
            }
            this.imagesEdited = true;
        }
    }

    //upload images, one by one
    uploadNextImage(): void {
        this.imageUploadComplete = false;
        // Check if there are any images left to upload
        let nextIndex = -1;
        for (let i = this.images.length - 1; i >= 0; i--) {
            if (!this.images[i].uploadID && this.images[i].uploadState !== -1 && this.images[i].uploadState !== 2) {
                nextIndex = i;
            }
        }

        if (nextIndex !== -1) {
            if (this.images[nextIndex].data) {
                this.images[nextIndex].uploadState = 1;
                this.documentService
                    .addImage(this.images[nextIndex].data!)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(
                        data => {
                            this.images[nextIndex].uploadState = 2;
                            this.images[nextIndex].uploadID = data.location;
                            this.uploadNextImage();
                        },
                        error => {
                            this.images[nextIndex].uploadState = -1;
                            console.log(error);
                            this.uploadNextImage();
                        }
                    );
            } else {
                this.images[nextIndex].uploadState = -1;
                this.uploadNextImage();
            }
        } else {
            this.imageUploadComplete = true;
        }
    }

    //delete images from blob storage
    deleteImages(): void {
        if (this.imagesToDelete.length > 0) {
            this.imagesToDelete.forEach((element, index) => {
                this.documentService
                    .removeImage(element)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(
                        () => { },
                        error => {
                            console.log(error);
                        }
                    );
            });
        }
    }
    ////End Image Functions////


    //when a user clicks save, delete images and upload unsaved images OR save the suite
    onClickSave(imgUploadModal: TemplateRef<any>) {
        this.saving = true;
        const options: HcToastOptions = {
            header: 'Save Started',
            body: 'Your changes are being saved and synced. This may take several seconds.',
            type: 'info',
            position: 'bottom-right'
        };
        this.toasterService.addToast(options);

        this.deleteImages();

        if (this.images.length > 0) {
            //make list of images that are not already saved
            let unsavedImages: FullImageElement[] = [];
            this.images.forEach(element => {
                if (element.uploadState != 2) {
                    unsavedImages.push(element);
                }
            });

            //if there are unsaved images, save them
            if (unsavedImages.length > 0) {
                const options: ModalOptions = {
                    size: 'lg',
                    ignoreOverlayClick: true,
                    ignoreEscapeKey: true,
                    data: { images: unsavedImages }
                };

                //open upload progress modal
                this.modalRef = this.modalService.open(imgUploadModal, options);
                this.uploadNextImage();
            } else {
                this.saveSuite();
            }
        } else {
            this.saveSuite();
        }
    }

    //// Check for changes in all suite attributes and log them ////
    logChanges(suite: SuiteElement) {
        let changes: ChangeElement[] = [];
        const date = new Date().toISOString();

        //check if name changed
        if (this.suite.name !== suite.name) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Name',
                previous: this.suite.name,
                update: suite.name,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        ////Checks for changes to previous names////
        //check for changes in previous names values
        const prevNamesObj: string[] = typeof this.suite.previousNames === 'object' ? this.suite.previousNames : JSON.parse(this.suite.previousNames);

        const prevStr = JSON.stringify(prevNamesObj);
        const updateStr = JSON.stringify(suite.previousNames);

        //if there's an previous name change, add it
        if (prevStr !== updateStr) {
            const prevNameChange = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Previous Names',
                previous: prevStr,
                update: updateStr,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(prevNameChange);
        }
        ////End checks for changes to previous names////

        //check if item changed
        if (this.suite.item !== suite.item) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Item ID',
                previous: this.suite.item.toString(),
                update: suite.item.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }


        //check if type changed

        //translate typeID into type name??
        if (this.suite.typeID !== suite.typeID) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Type',
                previous: this.suite.typeID.toString(),
                update: suite.typeID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if product line changed

        //translate productLineID into product line name??
        if (this.suite.productLineID !== suite.productLineID) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Product Line',
                previous: this.suite.productLineID.toString(),
                update: suite.productLineID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if status changed

        //translate statusID into status name??
        if (this.suite.statusID !== suite.statusID) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Status',
                previous: this.suite.statusID.toString(),
                update: suite.statusID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if sla changed

        //translate slaID into SLA name??
        if (this.suite.slaid !== suite.slaid) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'SLA',
                previous: this.suite.slaid.toString(),
                update: suite.slaid.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if published status changed
        if (this.suite.published !== suite.published) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Published Status',
                previous: this.suite.published.toString(),
                update: suite.published.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }


        //check if tagline changed
        if (this.suite.tagline !== suite.tagline) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Tagline',
                previous: this.suite.tagline,
                update: suite.tagline,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if problem changed
        if (this.suite.problem !== suite.problem) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Problem',
                previous: this.suite.problem,
                update: suite.problem,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if approach changed
        if (this.suite.approach !== suite.approach) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Approach',
                previous: this.suite.approach,
                update: suite.approach,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if features changed
        if (this.suite.features !== suite.features) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Benefits and Features',
                previous: this.suite.features,
                update: suite.features,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if short description changed
        if (this.suite.shortDescription !== suite.shortDescription) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Short Description',
                previous: this.suite.shortDescription,
                update: suite.shortDescription,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if subtitle changed
        if (this.suite.subtitle !== suite.subtitle) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Subtitle',
                previous: this.suite.subtitle,
                update: suite.subtitle,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if Solution Area changed
        if (this.suite.solutionArea !== suite.solutionArea) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Solution Area',
                previous: this.suite.solutionArea ? this.suite.solutionArea.toString() : 'none',
                update: suite.solutionArea.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if Solution Capability changed
        if (this.suite.solutionCapability !== suite.solutionCapability) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Solution Capability',
                previous: this.suite.solutionCapability ? this.solutionCapabilitiesList.flat().find(c => c.id === this.suite.solutionCapability)!.name : 'none',
                update: this.solutionCapabilitiesList.flat().find(c => c.id === suite.solutionCapability)!.name,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        ////Checks for changes to images////
        //check for changes in image attributes
        let imagesChanged = false;

        const prevImgObj: ImageElement[] = typeof this.suite.images === 'object' ? this.suite.images : JSON.parse(this.suite.images);

        suite.images.forEach(element => {
            for (let i = 0; i < prevImgObj.length; i++) {
                if (element.id === prevImgObj[i].id) {
                    if (element.fileName !== prevImgObj[i].fileName) {
                        imagesChanged = true;
                    }

                    if (element.visibleInSheet !== prevImgObj[i].visibleInSheet) {
                        imagesChanged = true;
                    }

                    if (element.caption !== prevImgObj[i].caption) {
                        imagesChanged = true;
                    }

                    if (element.type !== prevImgObj[i].type) {
                        imagesChanged = true;
                    }

                    if (element.placement !== prevImgObj[i].placement) {
                        imagesChanged = true;
                    }
                }
            }
        });

        // check for change in amount of images
        if (suite.images && prevImgObj && suite.images.length !== prevImgObj.length) {
            imagesChanged = true;
        }

        //if there's an image change, add it
        if (imagesChanged) {
            const imageChange = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Images',
                previous: JSON.stringify(prevImgObj),
                update: JSON.stringify(suite.images),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(imageChange);
        }
        ////End checks for changes to images////

        ////Checks for changes to icons////
        //check for changes in icon attributes
        let iconsChanged = false;

        if (this.suite.icons.svgURL !== suite.icons.svgURL) {
            iconsChanged = true;
        } else if (this.suite.icons.pngURL !== suite.icons.pngURL) {
            iconsChanged = true;
        } else if (this.suite.icons.base64URL !== suite.icons.base64URL) {
            iconsChanged = true;
        }

        if (iconsChanged) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Icons',
                previous: JSON.stringify(this.suite.icons),
                update: JSON.stringify(suite.icons),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }
        ////End checks for changes to icons////

        //check if techDescription changed
        if (this.suite.techDescription !== suite.techDescription) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Technical Description',
                previous: this.suite.techDescription,
                update: suite.techDescription,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if docsURL changed
        if (this.suite.docsURL !== suite.docsURL) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Docs URL',
                previous: this.suite.docsURL,
                update: suite.docsURL,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if communityURL changed
        if (this.suite.communityURL !== suite.communityURL) {
            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Community URL',
                previous: this.suite.communityURL,
                update: suite.communityURL,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if associatedTechnology changed
        if (this.suite.associatedTechnology !== suite.associatedTechnology) {
            let prevAssocTech: string[] = [];
            let currentAssocTech: string[] = [];

            //only save the technology names in the change
            this.suite.associatedTechnology.forEach(element => {
                prevAssocTech.push(element.name);
            });

            suite.associatedTechnology.forEach(element => {
                currentAssocTech.push(element.name);
            });

            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Included Technology',
                previous: JSON.stringify(prevAssocTech),
                update: JSON.stringify(currentAssocTech),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if associatedServices changed
        if (this.suite.associatedServices !== suite.associatedServices) {
            let prevAssocServs: string[] = [];
            let currentAssocServs: string[] = [];

            //only save the service names in the change
            this.suite.associatedServices.forEach(element => {
                prevAssocServs.push(element.name);
            });

            suite.associatedServices.forEach(element => {
                currentAssocServs.push(element.name);
            });

            const change = {
                item: suite.item,
                name: suite.name,
                category: 'suite',
                field: 'Associated Services',
                previous: JSON.stringify(prevAssocServs),
                update: JSON.stringify(currentAssocServs),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        if (changes.length > 0) {
            this.changeService.add(changes);
        }
    }

    saveSuite() {
        //grab update date
        const date = new Date().toISOString();

        //turn full image element into image element
        let finalImages: ImageElement[] = [];
        this.images &&
            this.images.forEach(img => {
                const captionControl = 'caption' + img.id;
                const placementControl = 'placement' + img.id;

                if (img.uploadID) {
                    finalImages.push({
                        id: img.uploadID,
                        fileName: img.fileName,
                        visibleInSheet: img.placement !== 'Hidden',
                        type: img.type,
                        caption: this.imageForm.get(captionControl)!.value,
                        placement: this.imageForm.get(placementControl)!.value
                    });
                }
            });

        //turn icon URLs into object
        let iconURLS: IconElement = {
            'svgURL': this.suiteForm.controls['iconSVGControl'].value,
            'pngURL': this.suiteForm.controls['iconPNGControl'].value,
            'base64URL': this.suiteForm.controls['iconBase64Control'].value,
        }

        //turn previous names into object
        let previousNames: string[] = [];
        for (let i = 0; i < this.suiteForm.controls.previousNamesControl.length; i++) {
            previousNames.push(this.suiteForm.controls.previousNamesControl.controls[i].value);
        }


        //create the suite object to save
        const suite: SuiteElement = {
            suiteID: this.suiteID || -1,
            name: this.suiteForm.controls['nameControl'].value,
            item: this.suiteForm.controls['IDControl'].value!,
            typeID: this.suiteForm.controls['typeControl'].value!,
            productLineID: this.suiteForm.controls['productLineControl'].value!,
            statusID: this.suiteForm.controls['statusControl'].value!,
            slaid: this.suiteForm.controls['slaControl'].value!,
            published: this.suiteForm.controls['published'].value,
            revisionDate: date,
            tagline: this.suiteForm.controls['taglineControl'].value,
            problem: this.suiteForm.controls['problemControl'].value,
            approach: this.suiteForm.controls['approachControl'].value,
            features: this.suiteForm.controls['benefitsFeaturesControl'].value,
            images: finalImages,
            icons: iconURLS,
            techDescription: this.suiteForm.controls['techDescControl'].value,
            docsURL: this.suiteForm.controls['docsUrlControl'].value,
            communityURL: this.suiteForm.controls['communityUrlControl'].value,
            associatedTechnology: this.suiteForm.controls['assocTechControl'].value,
            associatedServices: this.suiteForm.controls['assocServicesControl'].value,
            previousNames: previousNames,
            shortDescription: this.suiteForm.controls['shortDescription'].value,
            subtitle: this.suiteForm.controls['subtitle'].value,
            solutionArea: this.suiteForm.controls['solutionArea'].value,
            solutionCapability: this.suiteForm.controls['solutionCapability'].value
        };

        //if the suite is being edited, update it, log changes, and load changes
        if (this.suiteID) {
            this.suiteService
                .update(suite)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: () => {
                        this.logChanges(suite);
                        this.suiteService.refresh();
                        this.productService.refresh();
                        this.changeLog.loadChanges(this.itemID, "suite");
                        const options: HcToastOptions = {
                            header: 'Suite Updated',
                            body: suite.name + ' successfully updated.',
                            type: 'success',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);

                        //mark everything pristine, update suite variable, and update revision date
                        this.suiteForm.markAsPristine();
                        this.imageForm.markAsPristine();
                        this.imagesEdited = false;
                        this.suite = suite;
                        this.revisionDate = date;
                        this.saving = false;
                    },
                    error: msg => {
                        this.saving = false;
                        const options: HcToastOptions = {
                            header: 'Update Failed',
                            body: 'Unable to update suite. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        } else {
            //if the suite is being added, add it, log an add change, update the item ID, and load changes
            this.suiteService
                .add(suite)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: result => {

                        this.changeService.add([
                            {
                                item: suite.item,
                                name: suite.name,
                                category: 'suite',
                                field: '--',
                                previous: '--',
                                update: 'suite added',
                                user: this.oktaService.username.value,
                                date: new Date().toISOString()
                            }
                        ]);
                        this.suiteService.refresh();
                        this.suiteID = result;
                        this.itemID = suite.item;
                        this.savedItem = suite.item;
                        this.changeLog.loadChanges(this.itemID, "suite");
                        const options: HcToastOptions = {
                            header: 'Suite Added',
                            body: suite.name + ' successfully added.',
                            type: 'success',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);

                        //mark everything pristine and update suite variable
                        this.suiteForm.markAsPristine();
                        this.imageForm.markAsPristine();
                        this.imagesEdited = false;
                        this.suite = suite;
                        this.revisionDate = date;
                        this.saving = false;
                    },
                    error: msg => {
                        this.saving = false;
                        const options: HcToastOptions = {
                            header: 'Add Failed',
                            body: 'Unable to add suite. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        }
    }

    openLightbox(img: FullImageElement) {
        const options: ModalOptions = {
            size: 'auto',
            closeIcon: true,
            data: {
                img
            }
        };

        this.modalService.open(LightboxComponent, options);
    }

    openPdf(): void {
        window.open('/viewer?id=' + this.itemID.toString(), '_blank');
    }

    generateLink() {
        const link = environment.root.url + '/viewer?id=' + this.itemID.toString();
        navigator.clipboard.writeText(link);

        const options: HcToastOptions = {
            header: 'Copied pdf URL for ' + this.suite.name,
            type: 'info',
            position: 'bottom-center'
        };
        this.toasterService.addToast(options);
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    tabClick(selected: TabChangeEvent) {
        //ensure that newest changes are loaded if a user just updated
        selected.index === 1 && this.changeLog.loadChanges(this.itemID, "suite");
        this.selectedTab = selected.index;
    }

    canDeactivate(): boolean {
        return !this.suiteInputsDirty;
    }

    defaultSolutionCapability(): void {
        this.suiteForm.controls['solutionCapability'].setValue( this.solutionCapabilitiesList[this.suiteForm.controls['solutionArea'].value - 1][0].id );
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
